import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft } from "lucide-react";
import { useLeadModal } from "@/context/LeadModalContext";
import { steps, StepChi, StepAttivita, StepInteressi } from "./leadSteps";
import { useLeadForm } from "./useLeadForm";

const submitLabel = (submitting, isLast) => {
  if (submitting) return "INVIO IN CORSO...";
  return isLast ? "INVIA LA MIA RICHIESTA" : "CONTINUA";
};

const ProgressBar = ({ step }) => (
  <div data-testid="lead-form-progress" className="mb-8 flex justify-between border-b hairline pb-3">
    {steps.map((s, i) => (
      <span key={s.key} data-testid={`lead-form-step-label-${i + 1}`} className={`font-sans-ui text-[10px] tracking-mega transition-colors ${i <= step ? "text-ink" : "text-ink/35"}`}>
        {String(i + 1).padStart(2, "0")} — {s.label.toUpperCase()}
      </span>
    ))}
  </div>
);

const ThankYou = ({ onClose }) => (
  <div data-testid="lead-thank-you-state" className="py-8">
    <p className="font-sans-ui text-[11px] tracking-mega text-goldtan mb-6">RICHIESTA RICEVUTA</p>
    <h3 className="font-display font-light text-4xl sm:text-5xl text-ink leading-tight mb-6">La tua richiesta è arrivata.</h3>
    <div className="space-y-4 font-sans-ui text-sm sm:text-base text-ink/70 leading-relaxed max-w-lg">
      <p>Grazie per l'interesse verso My Brand Online.</p>
      <p>Stiamo selezionando i primi progetti che accederanno alla nostra infrastruttura.</p>
      <p>Analizzeremo le informazioni che ci hai inviato e ti contatteremo per conoscere meglio il tuo progetto.</p>
    </div>
    <p className="mt-10 font-display italic text-2xl sm:text-3xl text-ink leading-snug">
      Tu costruisci il brand.<br />Noi possiamo costruire ciò che c'è dietro.
    </p>
    <button onClick={onClose} className="mt-10 inline-flex items-center bg-ink text-ivory px-8 py-4 text-[11px] tracking-mega font-medium hover:bg-charcoal transition-colors">
      CHIUDI
    </button>
  </div>
);

export const LeadModal = () => {
  const { open, closeModal } = useLeadModal();
  const { form, step, dir, submitting, error, done, set, toggleInterest, back, submit, isLast } = useLeadForm(open);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const StepView = [StepAttivita, StepInteressi, StepChi][step];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-lenis-prevent
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center"
          style={{ height: "100dvh" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
        >
          <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={closeModal} />
          <motion.div
            data-testid="lead-form-modal"
            data-lenis-prevent
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full sm:max-w-2xl bg-ivory flex flex-col overflow-hidden h-[100dvh] sm:h-auto sm:max-h-[90dvh]"
          >
            <div className="shrink-0 flex items-center justify-between px-5 sm:px-12 h-14 border-b hairline bg-ivory" style={{ paddingTop: "env(safe-area-inset-top)" }}>
              <p className="font-sans-ui text-[11px] tracking-mega text-ink/70">
                {done ? "MY BRAND ONLINE" : <>EARLY ACCESS · <span className="text-ink font-semibold">PASSO {step + 1} DI {steps.length}</span></>}
              </p>
              <button
                type="button"
                onClick={closeModal}
                data-testid="lead-form-close"
                aria-label="Chiudi"
                className="-mr-2 inline-flex items-center gap-2 px-3 min-h-[44px] text-ink hover:bg-linen transition-colors"
              >
                <span className="sm:hidden font-sans-ui text-[10px] tracking-mega">CHIUDI</span>
                <X size={22} />
              </button>
            </div>
            {!done && (
              <div data-testid="lead-form-progressbar" className="shrink-0 h-[3px] bg-ink/10" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(((step + 1) / steps.length) * 100)}>
                <motion.div
                  data-testid="lead-form-progressbar-fill"
                  className="h-full bg-goldtan"
                  initial={false}
                  animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            )}

            {done ? (
              <div data-lenis-prevent className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 sm:px-12 py-6 sm:py-10" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}>
                <ThankYou onClose={closeModal} />
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col flex-1 min-h-0 overflow-hidden" data-testid={`lead-form-step-${step + 1}`}>
                <div
                  data-testid="lead-form-scroll-area"
                  data-lenis-prevent
                  className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 sm:px-12 py-8 sm:py-10 space-y-8"
                  style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
                >
                  <div>
                    <h3 className="font-display font-light text-3xl sm:text-4xl text-ink leading-tight">{steps[step].title}</h3>
                    <p className="mt-3 font-sans-ui text-sm text-ink/60">{steps[step].sub}</p>
                  </div>

                  <ProgressBar step={step} />

                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 24 * dir }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 * dir }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="space-y-8"
                    >
                      <StepView form={form} set={set} toggleInterest={toggleInterest} />
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div data-testid="lead-form-footer" className="shrink-0 bg-ivory border-t hairline px-5 sm:px-12 py-4 sm:py-5" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
                  {error && <p data-testid="lead-form-error" className="font-sans-ui text-sm text-destructive mb-3">{error}</p>}
                  <div className="flex items-center gap-4">
                    {step > 0 && (
                      <button type="button" onClick={back} data-testid="lead-form-back-button" className="inline-flex items-center gap-2 font-sans-ui text-[11px] tracking-mega text-ink/55 hover:text-ink transition-colors min-h-[44px] px-2 shrink-0">
                        <ArrowLeft size={14} /> INDIETRO
                      </button>
                    )}
                    <button
                      type="submit"
                      data-testid={isLast ? "lead-form-submit-button" : "lead-form-next-button"}
                      disabled={submitting}
                      className="group ml-auto flex-1 sm:flex-none inline-flex items-center justify-center gap-3 bg-ink text-ivory px-8 sm:px-10 py-4 text-[11px] tracking-mega font-semibold hover:bg-charcoal transition-colors disabled:opacity-50"
                    >
                      {submitLabel(submitting, isLast)}
                      {!submitting && <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />}
                    </button>
                  </div>
                  <p className="mt-3 font-sans-ui text-[11px] text-ink/45">
                    Nessun impegno. Ti rispondiamo entro 24-48 ore con una prima valutazione sul tuo caso specifico.
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
